import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { slideAction, saveMirrorToCloudinary } from '../../store/slideSlice';
import { slideAPI } from '../../store/api';
import { emitSmartArtNodesUpdate } from '../../Socket/Socket';
import { getSnapshot } from '../../utils/utils';
import { updateTextboxDelta } from '../../utils/helper';
import { normalizeNodes } from './smartArtUtils';

/**
 * Shared persistence for SmartArt edits — used by both the on-canvas object (inline editing)
 * and the side text pane. Updates Redux immediately, debounces the DB write, snapshots the
 * slide, and broadcasts over the socket.
 */
export const useSmartArtPersist = (smartArtId, currentSlideId) => {
  const { id: presentationId } = useParams();
  const dispatch = useDispatch();
  const isMasterSlide = useSelector((state) => state.slide.isMasterSlide);
  const masterSlideId = useSelector((state) => state.slide.masterSlideId);
  const existingSlides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);

  const persist = useRef(
    debounce((nextNodes, slideId, masterMode, masterId, slides, mirrors) => {
      const targetSlide = masterMode ? masterId : slideId;
      const payload = { currentSlideId: targetSlide, smartArtId, nodes: nextNodes };
      let updatedSlides = slides;
      try {
        updatedSlides = updateTextboxDelta(slides, payload, 'smartart');
      } catch (e) {
        updatedSlides = slides;
      }
      slideAPI.updateSmartArtNodesInDatabase(
        presentationId,
        targetSlide,
        smartArtId,
        nextNodes,
        masterMode ? undefined : dispatch,
        { slides: updatedSlides, mirrorSlides: mirrors }
      );
      if (!masterMode) {
        emitSmartArtNodesUpdate({
          roomId: presentationId,
          elementData: { slideId, smartArtId, nodes: nextNodes },
        });
      }
    }, 500)
  ).current;

  // snapshotting is debounced so live typing in the text pane doesn't fire one per keystroke
  const snapshot = useRef(
    debounce((slideId, pId) => {
      getSnapshot().then((dataUrl) => {
        dispatch(
          saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: pId })
        );
      });
    }, 500)
  ).current;

  const commitNodes = useCallback(
    (nextNodes) => {
      const normalized = normalizeNodes(nextNodes);
      dispatch(
        slideAction.updateSmartArtNodes({ slideId: currentSlideId, smartArtId, nodes: normalized })
      );
      persist(normalized, currentSlideId, isMasterSlide, masterSlideId, existingSlides, mirrorSlides);
      snapshot(currentSlideId, presentationId);
    },
    [dispatch, currentSlideId, smartArtId, isMasterSlide, masterSlideId, existingSlides, mirrorSlides, persist, snapshot, presentationId]
  );

  const commitLayout = useCallback(
    ({ layout, variant, theme }) => {
      dispatch(
        slideAction.updateSmartArtLayout({ slideId: currentSlideId, smartArtId, layout, variant, theme })
      );
      // ride the node-save endpoint so the whole document (incl. the new layout) is persisted
      const obj = existingSlides[currentSlideId]?.smartArtObjects?.[smartArtId];
      const merged = {
        ...obj,
        ...(layout !== undefined ? { layout } : {}),
        ...(variant !== undefined ? { variant } : {}),
        ...(theme !== undefined ? { theme } : {}),
      };
      const updatedSlides = {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          smartArtObjects: {
            ...existingSlides[currentSlideId].smartArtObjects,
            [smartArtId]: merged,
          },
        },
      };
      slideAPI.updateSmartArtNodesInDatabase(
        presentationId,
        currentSlideId,
        smartArtId,
        {
          nodes: merged.nodes || [],
          ...(merged.layout !== undefined ? { layout: merged.layout } : {}),
          ...(merged.variant !== undefined ? { variant: merged.variant } : {}),
          ...(merged.theme !== undefined ? { theme: merged.theme } : {}),
        },
        isMasterSlide ? undefined : dispatch,
        { slides: updatedSlides, mirrorSlides }
      );
      snapshot(currentSlideId, presentationId);
    },
    [dispatch, currentSlideId, smartArtId, existingSlides, mirrorSlides, presentationId, isMasterSlide, snapshot]
  );

  return { commitNodes, commitLayout };
};

export default useSmartArtPersist;
